import {
  extractTestsFromModule,
  Test,
  TestParserFlags,
  TestParserOptions,
  TestType,
} from 'esm-test-parser';
import * as Mocha from 'mocha';
import { builtInFunctions, MochaUiEsmOptions } from './mochaUiEsmOptions.js';

/**
 * Extracts test definitions from an ESM module and adds them to a Mocha suite.
 *
 * This function uses `esm-test-parser` to identify tests, groups, and built-in hooks
 * exported from the module and registers them with Mocha.
 *
 * @param rootSuite - The top-level Mocha suite to add tests to.
 * @param options - Configuration options for the ESM UI.
 * @param moduleToParse - The ESM module object to extract tests from.
 */
export function getTestsFromEsmModule(
  rootSuite: Mocha.Suite,
  options: MochaUiEsmOptions,
  moduleToParse: any,
): void {
  // create the suite map and add the root suite
  const suiteMap: Record<string, Mocha.Suite> = {
    root: rootSuite,
  };

  // create esm parser options
  const esmParserOptions: Partial<TestParserOptions> = {
    // mocha built in function names
    builtInFunctions,
    // class instance property name to access the mocha context
    classContextPropertyName: options.classContextPropertyName,
    // map the suite context to each test function or class instance
    testContextLookup: (suiteName: string) => suiteMap[suiteName].ctx,
    // map parser flags
    parserFlags: new TestParserFlags(options.parserFlags || {}),
    // sorting
    sort: options.sort,
  };

  if (options.testCaseTitleTemplate) {
    esmParserOptions.testCaseTitleTemplate = options.testCaseTitleTemplate;
  }

  // extract tests from the module
  const tests = extractTestsFromModule(moduleToParse, esmParserOptions);

  // iterate the test properties
  tests.forEach((test: Test) => {
    // check if this is a suite
    if (test.isGroup) {
      createSuite(suiteMap, test);
      return;
    }

    // check if this is a built in function
    if (test.type.has(TestType.BUILTIN_FUNCTION)) {
      const suite = suiteMap[test.groupName];
      (suite as any)[test.name](test.value);
      return;
    }

    // check if this is a test function
    if (test.type.has(TestType.FUNCTION)) {
      const mochaTest = new Mocha.Test(test.name, test.value);

      // get the test function suite and add the test
      const suite = suiteMap[test.groupName];
      suite.addTest(mochaTest);

      // set the test as only
      if (test.isOnly) (suite as any).appendOnlyTest(mochaTest);

      return;
    }
  });
}

/**
 * Creates a new Mocha sub-suite based on the provided test definition.
 *
 * It manages sub-suite naming and avoids duplicate suite creation.
 *
 * @param suiteMap - A map of existing Mocha suites, indexed by a unique string.
 * @param test - The test group definition from the parser.
 */
function createSuite(suiteMap: Record<string, Mocha.Suite>, test: any): void {
  const parentSuiteName = test.groupName;
  const childSuiteName = parentSuiteName + '_' + test.name;

  // check if the child suite exists already
  const suiteExists = Object.hasOwn(suiteMap, childSuiteName);
  if (suiteExists) return;

  // create a new child suite
  const parentSuite = suiteMap[parentSuiteName];
  const childSuite = Mocha.Suite.create(parentSuite, test.name);

  // add the new child suite to the map
  suiteMap[childSuiteName] = childSuite;

  // set the suite as only in the parent suite
  if (test.isOnly) (parentSuite as any).appendOnlySuite(childSuite);
}
